import {
  CheckRounded,
  KeyboardArrowDown,
  Link as LinkIcon,
  PersonAddAltRounded,
} from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Switch,
  TextField,
} from '@mui/material';
import produce from 'immer';
import React, { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react';
import { canModifyUserRole, TripRole } from '../../../firebase/firebase.types';
import { useEmailValidation, useUserRoles } from '../../../hooks/trip-share.hooks';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import useTimeout from '../../../hooks/useTimeout';
import useToast from '../../../hooks/useToast';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateTrip } from '../../../redux/trip/trip.slice';
import { TripRolesAssignable } from '../../../redux/trip/trip.types';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { TripShareProps } from '../../../types/TripShare';
import { getVisibleDisplayName } from '../../../utils/accountConstants';
import {
  copyTextToClipboard,
  doesUserEmailAlreadyHaveAccess,
  getRoleForUser,
  roleDescriptions,
  validateEmail,
} from '../../../utils/trip-share.utils';
import LetterAvatar from '../../LetterAvatar/LetterAvatar';
import LetterAvatarGroup from '../../LetterAvatar/LetterAvatarGroup';
import './TripShare.scss';

function TripShare({ trip, canEdit, open: controlledOpen, onClose }: TripShareProps) {
  const dispatch = useAppDispatch();
  const { error: toastError } = useToast();
  const isMobile = useMobileMediaQuery();
  const currentUser = useAppSelector(selectCurrentUser);
  const [internalOpen, setOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const [isPublic, setIsPublic] = useState(trip.public);
  const [isCopied, setIsCopied] = useState(false);
  const resetCopied = useTimeout();
  const [roleMenuAnchorEl, setRoleMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { userRoles, setUserRoles, hasPendingChanges, isSaving, setIsSaving, saveChanges } =
    useUserRoles(trip.id, trip.roles);

  const {
    emailInput,
    setEmailInput,
    emailError,
    setEmailError,
    isCheckingEmail,
    setIsCheckingEmail,
  } = useEmailValidation(userRoles, setUserRoles);

  const userEmails = useMemo(() => userRoles.map((role) => role.email), [userRoles]);
  const displayNames = useMemo(
    () => Object.fromEntries(userRoles.map((role) => [role.email, role.displayName])),
    [userRoles]
  );
  const roleMenuOpen = Boolean(roleMenuAnchorEl);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    onClose?.();
    setEmailInput('');
    setEmailError(null);
  }, [setEmailInput, setEmailError, onClose]);

  const handleSaveClick = useCallback(() => {
    if (hasPendingChanges) {
      setIsSaving(true);
      saveChanges().then((success) => {
        if (success) {
          handleClose();
        }
      });
    } else {
      handleClose();
    }
  }, [hasPendingChanges, saveChanges, handleClose, setIsSaving]);

  const handleRoleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setRoleMenuAnchorEl(event.currentTarget);
  }, []);

  const handleRoleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLLIElement>, newRole: TripRole) => {
      if (!roleMenuAnchorEl || !currentUser) {
        return;
      }

      const targetUserId = roleMenuAnchorEl.id;
      const canChange = canModifyUserRole(
        trip.roles[currentUser.uid],
        targetUserId,
        newRole,
        trip.roles
      );

      if (!canChange) {
        toastError('You do not have permission to make this role change.');
        setRoleMenuAnchorEl(null);
        return;
      }

      const index = userRoles.findIndex((userAndRole) => userAndRole.userId === targetUserId);
      if (index >= 0) {
        const newUserRoles = produce(userRoles, (state) => {
          state[index].role = newRole;
        });
        setUserRoles(newUserRoles);
      }
      setRoleMenuAnchorEl(null);
    },
    [roleMenuAnchorEl, currentUser, trip.roles, userRoles, setUserRoles, toastError]
  );

  const handleRemoveUserClick = useCallback(() => {
    if (!roleMenuAnchorEl) {
      return;
    }
    const index = userRoles.findIndex((userAndRole) => userAndRole.userId === roleMenuAnchorEl.id);
    if (index >= 0) {
      const newUserRoles = produce(userRoles, (state) => {
        state.splice(index, 1);
      });
      setUserRoles(newUserRoles);
    }
    setRoleMenuAnchorEl(null);
  }, [roleMenuAnchorEl, userRoles, setUserRoles]);

  const handleRoleMenuClose = useCallback(() => {
    setRoleMenuAnchorEl(null);
  }, []);

  const handlePublicChange = useCallback(() => {
    const newValue = !isPublic;
    dispatch(updateTrip({ tripId: trip.id, field: 'public', value: newValue }));
    setIsPublic(newValue);
  }, [dispatch, trip.id, isPublic]);

  const handleSubmit = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      if (validateEmail(emailInput)) {
        if (doesUserEmailAlreadyHaveAccess(userRoles, emailInput)) {
          setEmailError(`${emailInput} already has access to this trip`);
        } else {
          setEmailError(null);
          setIsCheckingEmail(true);
        }
      } else {
        setEmailError('Invalid email');
      }
    },
    [emailInput, userRoles, setEmailError, setIsCheckingEmail]
  );

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmailInput(event.target.value);
    },
    [setEmailInput]
  );

  const handleCopyClick = useCallback(() => {
    copyTextToClipboard(window.location.href)
      .then(() => {
        setIsCopied(true);
        resetCopied.set(() => setIsCopied(false), 1500);
      })
      .catch(() => {
        toastError('Failed to copy link to clipboard');
      });
  }, [toastError, resetCopied]);

  const mobileClass = useMemo(() => (isMobile ? ' mobile' : ''), [isMobile]);
  const spanClass = useMemo(() => `trip-share-modal-span${mobileClass}`, [mobileClass]);
  const switchLabelClass = useMemo(
    () =>
      ['trip-share-switch-label', isPublic && ' public', !canEdit && ' disabled']
        .filter(Boolean)
        .join(' '),
    [isPublic, canEdit]
  );

  return (
    <>
      {isMobile
        ? null
        : currentUser && (
            <div className='trip-share-container'>
              <LetterAvatarGroup
                userRoles={userEmails}
                displayNames={displayNames}
              />
              <Button
                className='trip-share-button button'
                variant='outlined'
                startIcon={<PersonAddAltRounded />}
                onClick={handleOpen}
              >
                Share
              </Button>
            </div>
          )}

      {open && (
        <Modal
          open
          onClose={handleClose}
        >
          <div className={`trip-share-modal-container${mobileClass}`}>
            <div className='trip-share-modal-subcontainer'>
              <h3 className='trip-share-header'>{canEdit && 'Add '}Collaborators</h3>
              {canEdit && (
                <form className='trip-share-add-form'>
                  <TextField
                    className='trip-share-text-field'
                    label='Email address'
                    variant='standard'
                    value={emailInput}
                    onChange={handleEmailChange}
                    error={emailError != null}
                    helperText={emailError}
                    disabled={isCheckingEmail}
                  />
                  <Button
                    className='trip-share-add-button'
                    type='submit'
                    variant='text'
                    onClick={handleSubmit}
                  >
                    Add ↵
                  </Button>
                </form>
              )}
              {userRoles.map((userAndRole) => (
                <div
                  className='trip-share-modal-row-container'
                  key={userAndRole.userId}
                >
                  <div className='trip-share-modal-row-group'>
                    <LetterAvatar
                      string={userAndRole.email}
                      displayName={userAndRole.displayName}
                      capitalize
                    />
                    <span className={`trip-share-email-span ${spanClass}`}>
                      {userAndRole.displayName && (
                        <div style={{ fontWeight: 500 }}>
                          {getVisibleDisplayName(userAndRole)}
                          {currentUser && userAndRole.userId === currentUser.uid && ' (You)'}
                        </div>
                      )}
                      <div>{userAndRole.email}</div>
                    </span>
                  </div>
                  {userAndRole.role === TripRole.OWNER || !canEdit ? (
                    <span className={`trip-share-collaborator-role ${spanClass}`}>
                      <div>{userAndRole.role}</div>
                    </span>
                  ) : (
                    <Button
                      className={`trip-share-role-button${isMobile ? ' mobile' : ''}`}
                      id={userAndRole.userId}
                      onClick={handleRoleClick}
                      endIcon={<KeyboardArrowDown />}
                    >
                      {userAndRole.role}
                    </Button>
                  )}
                </div>
              ))}
              {(hasPendingChanges || isSaving) && (
                <div className='trip-share-modal-row-group-end'>
                  {isSaving ? (
                    <Button
                      className='trip-save-done-button'
                      variant='outlined'
                    >
                      <CircularProgress size={24} />
                    </Button>
                  ) : (
                    <Button
                      className='trip-save-done-button'
                      variant='contained'
                      onClick={handleSaveClick}
                    >
                      Save
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className='trip-share-modal-subcontainer'>
              <div className='trip-share-modal-row-container'>
                <h3 className='trip-share-header'>Trip Access</h3>
                <Button
                  className='trip-copy-link-button button'
                  variant='contained'
                  onClick={handleCopyClick}
                  startIcon={<LinkIcon />}
                >
                  {isCopied ? 'Copied!' : 'Copy link'}
                </Button>
              </div>
              <div className='trip-share-modal-row-container'>
                <span className={`trip-share-link-settings-span ${spanClass}`}>
                  {isPublic
                    ? 'Anyone has access to view this trip'
                    : 'Only invited collaborators have access to this trip'}
                </span>
                <div className='trip-share-modal-switch-container'>
                  <FormControlLabel
                    value='public'
                    control={
                      <Switch
                        color='success'
                        checked={isPublic}
                        onChange={handlePublicChange}
                      />
                    }
                    label={
                      <span className={switchLabelClass}>{isPublic ? 'PUBLIC' : 'PRIVATE'}</span>
                    }
                    labelPlacement='start'
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {roleMenuOpen && (
        <Menu
          className='trip-role-menu'
          anchorEl={roleMenuAnchorEl}
          open={roleMenuOpen}
          onClose={handleRoleMenuClose}
        >
          {TripRolesAssignable.map((role) => (
            <MenuItem
              key={role}
              selected={
                roleMenuAnchorEl !== null && role === getRoleForUser(userRoles, roleMenuAnchorEl.id)
              }
              onClick={(event) => handleRoleMenuItemClick(event, role)}
            >
              <ListItemText
                className='trip-share-role-list-item-text'
                primary={role}
                secondary={roleDescriptions[role]?.description}
              />
              {roleMenuAnchorEl && role === getRoleForUser(userRoles, roleMenuAnchorEl.id) && (
                <ListItemIcon>
                  <CheckRounded />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            key='remove'
            onClick={handleRemoveUserClick}
          >
            <ListItemText className='trip-share-role-list-item-text'>Remove</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
}

TripShare.displayName = 'TripShare';
export default TripShare;
